import React, { useCallback, useEffect, useState } from "react";
import { Modal, Input, Button, Select, Checkbox, Tag, Space, Alert } from "antd";
import { useAuth0 } from "@auth0/auth0-react";
import { useSnackbar } from "notistack";
import { Gift, User, Mail, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import { getResellers, updateCustomerReferral } from "../../utils/users";

const UpdateCustomerReferralModal = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [referralCode, setReferralCode] = useState("");
  const [selectedResellerId, setSelectedResellerId] = useState(null);
  const [resellers, setResellers] = useState([]);
  const [loadingResellers, setLoadingResellers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  const fetchResellers = useCallback(async () => {
    try {
      setLoadingResellers(true);
      const token = await getIdTokenClaims();
      const res = await getResellers(token.__raw);
      if (res.data?.success && Array.isArray(res.data.resellers)) {
        // Filter active resellers with valid codes
        const activeResellers = res.data.resellers.filter(
          (r) => r.referral_code && (r.status === 1 || r.status === "1")
        );
        setResellers(activeResellers);
      }
    } catch (err) {
      console.error("Failed to load resellers:", err);
    } finally {
      setLoadingResellers(false);
    }
  }, [getIdTokenClaims]);

  // Load active resellers for the dropdown
  useEffect(() => {
    if (open) {
      fetchResellers();
    }
  }, [open, fetchResellers]);

  // Sync state when selected user changes or modal opens
  useEffect(() => {
    if (user && open) {
      const currentCode = user.customer_referral_code || "";
      setReferralCode(currentCode);
      setSendEmail(true);
    } else {
      setReferralCode("");
      setSelectedResellerId(null);
    }
  }, [user, open]);

  // When resellers list or current code updates, match selected reseller
  useEffect(() => {
    if (referralCode && resellers.length > 0) {
      const matched = resellers.find(
        (r) =>
          r.referral_code &&
          r.referral_code.trim().toUpperCase() === referralCode.trim().toUpperCase()
      );
      if (matched) {
        setSelectedResellerId(matched.id);
      } else {
        setSelectedResellerId(null);
      }
    } else {
      setSelectedResellerId(null);
    }
  }, [referralCode, resellers]);

  const handleSelectReseller = (resellerId) => {
    setSelectedResellerId(resellerId);
    const chosen = resellers.find((r) => r.id === resellerId);
    if (chosen && chosen.referral_code) {
      setReferralCode(chosen.referral_code);
    }
  };

  const matchedReseller = resellers.find(
    (r) =>
      r.referral_code &&
      r.referral_code.trim().toUpperCase() === referralCode.trim().toUpperCase()
  );

  const handleSave = async () => {
    const trimmedCode = referralCode.trim();
    if (!trimmedCode) {
      enqueueSnackbar("Please enter or select a referral code.", {
        variant: "warning",
      });
      return;
    }

    if (
      user?.email &&
      matchedReseller?.email &&
      user.email.toLowerCase() === matchedReseller.email.toLowerCase()
    ) {
      enqueueSnackbar("A user cannot be referred by their own referral code.", {
        variant: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const token = await getIdTokenClaims();
      const res = await updateCustomerReferral(
        token.__raw,
        user.id,
        trimmedCode,
        sendEmail
      );

      if (res.data?.success) {
        enqueueSnackbar(
          res.data.message || "Referral code updated successfully.",
          { variant: "success" }
        );
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        enqueueSnackbar(
          res.data?.message || "Failed to update referral code.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("Error updating referral code:", err);
      enqueueSnackbar(
        err.response?.data?.message || "An error occurred while updating referral code.",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        `Are you sure you want to remove the referral code for ${
          user?.name || user?.email
        }?`
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const token = await getIdTokenClaims();
      const res = await updateCustomerReferral(
        token.__raw,
        user.id,
        "", // empty code signals removal
        false
      );

      if (res.data?.success) {
        enqueueSnackbar(
          res.data.message || "Referral code removed successfully.",
          { variant: "success" }
        );
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        enqueueSnackbar(
          res.data?.message || "Failed to remove referral code.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("Error removing referral code:", err);
      enqueueSnackbar(
        err.response?.data?.message || "An error occurred while removing referral code.",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const currentCode = user?.customer_referral_code;

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!saving) onClose();
      }}
      centered
      width={560}
      title={
        <div className="flex items-center gap-2.5 text-lg font-semibold text-gray-900 border-b border-gray-100 pb-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Gift className="w-4 h-4" />
          </div>
          <span>
            {currentCode ? "Update Customer Referral Code" : "Assign Referral Code"}
          </span>
        </div>
      }
      footer={
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            {currentCode && (
              <Button
                danger
                type="text"
                icon={<Trash2 className="w-4 h-4" />}
                onClick={handleRemove}
                loading={saving}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
              >
                Remove Code
              </Button>
            )}
          </div>
          <Space>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {currentCode ? "Save Changes" : "Assign Code"}
            </Button>
          </Space>
        </div>
      }
    >
      <div className="py-2 space-y-4 text-sm text-gray-700">
        {/* Customer Information Card */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-medium text-gray-900">
              <User className="w-4 h-4 text-gray-500" />
              <span>{user?.name || "Customer"}</span>
            </div>
            {currentCode ? (
              <Tag color="blue" className="font-mono text-xs font-semibold m-0">
                Current: {currentCode}
              </Tag>
            ) : (
              <Tag color="default" className="text-xs text-gray-500 m-0">
                No Referral Code
              </Tag>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Mail className="w-3.5 h-3.5" />
            <span>{user?.email || "—"}</span>
          </div>
          {user?.referred_by_name && (
            <div className="text-xs text-gray-500 pt-1 border-t border-gray-200">
              Currently referred by:{" "}
              <span className="font-medium text-gray-700">
                {user.referred_by_name}
              </span>
              {user.referred_by_email ? ` (${user.referred_by_email})` : ""}
            </div>
          )}
        </div>

        {/* Instructions */}
        <p className="text-xs text-gray-600 leading-relaxed">
          Use this to attach or update a referral partner&apos;s code for this customer if they forgot to enter it during registration.
        </p>

        {/* Quick Select from Referral Partners */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Select Referral Partner
          </label>
          <Select
            showSearch
            placeholder="Search and select an active partner..."
            value={selectedResellerId}
            onChange={handleSelectReseller}
            loading={loadingResellers}
            className="w-full"
            filterOption={(input, option) =>
              (option?.label || "").toLowerCase().includes(input.toLowerCase())
            }
            options={resellers.map((r) => ({
              value: r.id,
              label: `${r.full_name} (${r.referral_code}) — ${r.email}`,
            }))}
            allowClear
            onClear={() => {
              setSelectedResellerId(null);
            }}
          />
        </div>

        {/* Referral Code Text Input */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Referral Code <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. PARTNER2025"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            className="font-mono text-sm tracking-wide uppercase"
          />
        </div>

        {/* Live Partner Verification Card */}
        {matchedReseller ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5 text-xs text-emerald-900">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-emerald-950">
                Verified Partner: {matchedReseller.full_name}
              </div>
              <div className="text-emerald-700 mt-0.5">
                Email: {matchedReseller.email} • Commission:{" "}
                {matchedReseller.referral_fee_percent}%
              </div>
            </div>
          </div>
        ) : referralCode.trim() ? (
          <Alert
            type="info"
            showIcon
            icon={<AlertCircle className="w-4 h-4" />}
            message="Custom Referral Code"
            description={`Code "${referralCode.trim()}" will be validated against active referral partners on submission.`}
            className="text-xs"
          />
        ) : null}

        {/* Send Email Option */}
        <div className="pt-1">
          <Checkbox
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="text-xs text-gray-700"
          >
            Send referral confirmation & coupon email to customer and notify partner
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateCustomerReferralModal;
